import { useState, useEffect, useCallback } from 'react';

export type ColorTheme = 'green' | 'warm' | 'blue' | 'purple' | 'forest';
export type DarkMode = 'light' | 'dark' | 'system';

interface ThemeSettings {
  colorTheme: ColorTheme;
  darkMode: DarkMode;
}

const THEME_STORAGE_KEY = 'korsordslexikon-theme';

const getStoredTheme = (): ThemeSettings => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return { colorTheme: 'green', darkMode: 'light' };
};

const getSystemDarkMode = (): boolean => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export function useTheme() {
  const [settings, setSettings] = useState<ThemeSettings>(getStoredTheme);

  const applyTheme = useCallback((theme: ThemeSettings) => {
    const root = document.documentElement;
    
    // Apply color theme
    root.classList.remove('theme-warm', 'theme-blue', 'theme-purple', 'theme-forest');
    if (theme.colorTheme !== 'green') {
      root.classList.add(`theme-${theme.colorTheme}`);
    }
    
    // Apply dark mode
    const isDark = theme.darkMode === 'dark' || 
      (theme.darkMode === 'system' && getSystemDarkMode());
    
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    applyTheme(settings);
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(settings));
  }, [settings, applyTheme]);

  // Listen for system dark mode changes
  useEffect(() => {
    if (settings.darkMode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme(settings);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [settings, applyTheme]);

  const setColorTheme = (colorTheme: ColorTheme) => {
    setSettings(prev => ({ ...prev, colorTheme }));
  };

  const setDarkMode = (darkMode: DarkMode) => {
    setSettings(prev => ({ ...prev, darkMode }));
  };

  return {
    colorTheme: settings.colorTheme,
    darkMode: settings.darkMode,
    setColorTheme,
    setDarkMode,
  };
}